import { AddressInput } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/AddressInput';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';
import { SingleTaxDetails } from '@dealstack/frontend/shared/components/SingleTaxDetails/SingleTaxDetails';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { groupedSelectDividerStyles } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { COUNTRIES_NAME_CODE_GROUPED_OPTIONS } from '@dealstack/shared/lib/countries/countriesData';
import { PersonRelationType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { ActionIcon, Button, Divider, Flex, Text } from '@mantine/core';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { MultiSelect, Select, TextInput } from 'react-hook-form-mantine';
import styled from 'styled-components';

import {
	CONTROLLING_PARTY_RELATION_TYPES,
	controllingPartyFieldName,
	controllingPartyTaxFieldName,
	createEmptyControllingPartyTaxResidence,
} from './controllingParties.utils';
import { useControllingPartyEmailLookup } from './useControllingPartyEmailLookup';
import type { ControllingPartiesFormSlice } from './useJoinAndAllocationPIVInformationController.types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

const CountryOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_COUNTRY_OPTION);

interface Props {
	index: number;
	onRemove: () => void;
	disabled?: boolean;
	isDuplicate?: boolean;
}

export const ControllingPartyEntryFields = ({ index, onRemove, disabled, isDuplicate }: Props) => {
	const tInvestor = useTranslationInvestor();
	const { prefillFromEmail } = useControllingPartyEmailLookup();
	const { control, setValue } = useFormContext<ControllingPartiesFormSlice>();

	const relationType = useWatch({
		control,
		name: controllingPartyFieldName(index, 'relationshipType'),
	});
	const isOtherRelationType = relationType === PersonRelationType.OTHER;

	// Any named role describes itself, so the free-text label is dropped on switch —
	// otherwise a stale role would linger in the form and reappear on switching back.
	// The revealed input is deliberately NOT validated here: untouched fields stay
	// clean until the investor interacts or attempts to continue.
	const onRelationTypeChange = (value: string | null) => {
		if (value !== PersonRelationType.OTHER) {
			setValue(controllingPartyFieldName(index, 'customRelationType'), '', {
				shouldValidate: true,
				shouldDirty: true,
			});
		}
	};

	const relationTypeOptions = CONTROLLING_PARTY_RELATION_TYPES.map((type) => ({
		value: type,
		label: tInvestor(`investment.piv.controlling_parties.relation_type.${type.toLowerCase()}`),
	}));

	const {
		fields: taxFields,
		append: appendTaxResidence,
		remove: removeTaxResidence,
	} = useFieldArray({
		control,
		name: controllingPartyFieldName(index, 'taxResidences'),
	});

	return (
		<EntryCard>
			<Flex justify="space-between" align="center" mb="0.5rem">
				<Text size="B5Bold">
					{tInvestor('investment.piv.controlling_parties.entry_title', {
						number: index + 1,
					})}
				</Text>
				<Button variant="subtle" color="red" onClick={onRemove} disabled={disabled}>
					{tInvestor('investment.piv.controlling_parties.remove')}
				</Button>
			</Flex>

			<Flex direction="column" gap="1rem">
				<TextInput
					name={controllingPartyFieldName(index, 'email')}
					label={tInvestor('investment.piv.controlling_parties.email')}
					placeholder={tInvestor('investment.piv.controlling_parties.email')}
					size="md"
					disabled={disabled}
					onBlur={(event) => prefillFromEmail(index, event.currentTarget.value)}
				/>

				<Flex gap="1rem">
					<TextInput
						name={controllingPartyFieldName(index, 'firstName')}
						label={tInvestor('investment.piv.controlling_parties.first_name')}
						placeholder={tInvestor('investment.piv.controlling_parties.first_name')}
						size="md"
						disabled={disabled}
						style={{ flex: 1 }}
					/>
					<TextInput
						name={controllingPartyFieldName(index, 'lastName')}
						label={tInvestor('investment.piv.controlling_parties.last_name')}
						placeholder={tInvestor('investment.piv.controlling_parties.last_name')}
						size="md"
						disabled={disabled}
						style={{ flex: 1 }}
					/>
				</Flex>

				<Divider my="0.5rem" />

				<AddressInput
					countryFieldName={controllingPartyFieldName(index, 'country')}
					cityFieldName={controllingPartyFieldName(index, 'city')}
					postCodeFieldName={controllingPartyFieldName(index, 'postCode')}
					streetFieldName={controllingPartyFieldName(index, 'street')}
					houseNumberFieldName={controllingPartyFieldName(index, 'houseNumber')}
					disabled={disabled}
					title={tInvestor('investment.piv.controlling_parties.address')}
				/>

				<Divider my="0.5rem" />

				<MultiSelect
					name={controllingPartyFieldName(index, 'citizenship')}
					label={tInvestor('investment.piv.controlling_parties.nationalities')}
					placeholder={tInvestor('investment.piv.controlling_parties.nationalities')}
					data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
					renderOption={CountryOption}
					styles={groupedSelectDividerStyles}
					searchable
					size="md"
					disabled={disabled}
				/>

				<Flex gap="1rem">
					<MantineDateInput
						name={controllingPartyFieldName(index, 'dateOfBirth')}
						label={tInvestor('investment.piv.controlling_parties.date_of_birth')}
						maxDate={new Date()}
						size="md"
						disabled={disabled}
						style={{ flex: 1 }}
					/>
					<TextInput
						name={controllingPartyFieldName(index, 'placeOfBirth')}
						label={tInvestor('investment.piv.controlling_parties.place_of_birth')}
						placeholder={tInvestor('investment.piv.controlling_parties.place_of_birth')}
						size="md"
						disabled={disabled}
						style={{ flex: 1 }}
					/>
				</Flex>

				<Select
					name={controllingPartyFieldName(index, 'countryOfBirth')}
					label={tInvestor('investment.piv.controlling_parties.country_of_birth')}
					placeholder={tInvestor('investment.piv.controlling_parties.country_of_birth')}
					data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
					renderOption={CountryOption}
					styles={groupedSelectDividerStyles}
					searchable
					size="md"
					disabled={disabled}
				/>

				<Divider my="0.5rem" />

				<Flex direction="column">
					{taxFields.map((taxField, taxIndex) => (
						<SingleTaxDetails
							key={taxField.id}
							withHeader={taxIndex === 0}
							taxResidencyFieldName={controllingPartyTaxFieldName(
								index,
								taxIndex,
								'taxResidency'
							)}
							tinFieldName={controllingPartyTaxFieldName(index, taxIndex, 'tin')}
							disabled={disabled}
							headerInfoText={
								taxIndex === 0
									? tInvestor('investment.piv.controlling_parties.tax_info')
									: undefined
							}
							rightSection={
								<ActionIcon
									variant="subtle"
									color="red"
									size="md"
									h="40px"
									onClick={() => removeTaxResidence(taxIndex)}
									disabled={disabled || taxFields.length <= 1}
								>
									<Icon variant="trash-can" size="lg" />
								</ActionIcon>
							}
						/>
					))}
					<div>
						<Button
							mt="1rem"
							variant="default"
							onClick={() =>
								appendTaxResidence(createEmptyControllingPartyTaxResidence(), {
									shouldFocus: false,
								})
							}
							disabled={disabled}
						>
							{tInvestor('investment.piv.controlling_parties.tax_residences.add')}
						</Button>
					</div>
				</Flex>

				<Divider my="0.5rem" />

				<Flex direction="column" gap="0.25rem">
					<Select
						name={controllingPartyFieldName(index, 'relationshipType')}
						label={tInvestor('investment.piv.controlling_parties.relation_type.label')}
						placeholder={tInvestor(
							'investment.piv.controlling_parties.relation_type.label'
						)}
						data={relationTypeOptions}
						size="md"
						disabled={disabled}
						onChange={onRelationTypeChange}
					/>
					{isOtherRelationType && (
						<TextInput
							name={controllingPartyFieldName(index, 'customRelationType')}
							label={tInvestor(
								'investment.piv.controlling_parties.custom_relation_type.label'
							)}
							placeholder={tInvestor(
								'investment.piv.controlling_parties.custom_relation_type.placeholder'
							)}
							size="md"
							disabled={disabled}
							mt="0.5rem"
						/>
					)}
					{isDuplicate && (
						<Text size="B6" color="red">
							{tInvestor('investment.piv.controlling_parties.duplicate_role_error')}
						</Text>
					)}
				</Flex>
			</Flex>
		</EntryCard>
	);
};

const EntryCard = styled.div`
	padding: 1.5rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 0.5rem;
`;
